<template>
  <div class="container mx-auto mt-3">
      <!-- Nav tabs -->
        <Tabs :value="tabValue" @update:value="updateUrl">
          <TabList>
              <Tab :value="route('admin.user.index')">
                  {{ $t('messages.admin.users') }}
              </Tab>
              <Tab :value="route('admin.company.index')">
                  {{ $t('messages.admin.companies') }}
              </Tab>
              <Tab :value="route('admin.template.index')">
                  {{ $t('messages.admin.templates') }}
              </Tab>
              <!-- RED-384: the report quality check's settings; REdata admins only, behind its flag -->
              <Tab v-if="reportQcEnabled" :value="route('admin.report-qc.index')" data-testid="admin-tab-report-qc">
                  {{ $t('messages.admin.report_qc.tab') }}
              </Tab>
              <!-- RED-317: the company's market overviews, behind its flag -->
              <Tab v-if="marketOverviewsEnabled" :value="route('admin.market-overview.index')" data-testid="admin-tab-market-overviews">
                  {{ $t('messages.admin.market_overviews.tab') }}
              </Tab>
              <Tab :value="route('admin.template-option.index')">
                  {{ $t('messages.admin.templateOption.name') }}
              </Tab>
              <Tab :value="route('admin.prediction-traces.index')">
                  {{ $t('messages.admin.predictions') }}
              </Tab>
              <Tab :value="route('admin.model-explorer.index')">
                  {{ $t('messages.admin.model_explorer') }}
              </Tab>
              <Tab :value="route('admin.similarity-calibration.index')">
                  {{ $t('messages.admin.similarity_calibration') }}
              </Tab>
              <Tab :value="route('admin.accuracy-scan.index')">
                  {{ $t('messages.admin.accuracy_scan') }}
              </Tab>
              <Tab :value="route('admin.role.index')">
                  {{ $t('messages.admin.roles_permissions') }}
              </Tab>
              <Tab :value="route('admin.feature-flag.index')">
                  {{ $t('messages.admin.feature_flags.tab') }}
              </Tab>
              <Tab :value="route('admin.notebook.index')">
                  {{ $t('messages.admin.notebooks.tab') }}
              </Tab>
          </TabList>
        </Tabs>
        <div class="px-4">
          <slot></slot>
        </div>
  </div>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useFeature } from '@/composables/useFeature.js'
import { NAVIGATION_KEPT_EVENT } from '@/composables/useUnsavedGuard.js'

const page = usePage()
const marketOverviewsEnabled = useFeature('market-overviews')
const reportQcFlag = useFeature('report-qc')
// The route 403s for company admins, so they never see the tab either.
const reportQcEnabled = computed(() => reportQcFlag.value && page.props.auth?.can?.admin === true)

// Keeps the parent tab highlighted on nested routes: /admin/user/{id}/edit
// highlights Kasutajad, /admin/report-qc/checks/{id} (RED-389) Aruande
// kontroll. A nested route may need parameters, so never `route(currentName)`
// for it — Ziggy throws on a missing parameter; its group's index needs none.
const activeTabValue = () => {
  const currentName = route().current()
  if (!currentName) {
    return ''
  }
  const groupIndex = currentName.replace(/^(admin\.[^.]+)\..+$/, '$1.index')
  if (groupIndex !== currentName && route().has(groupIndex)) {
    return route(groupIndex)
  }
  return route(currentName)
}

// A ref, not activeTabValue() in the template: when a leave-guard holds a
// navigation back (RED-384), the tab bar must drop the tab it already moved to.
const tabValue = ref(activeTabValue())
watch(() => page.url, () => {
  tabValue.value = activeTabValue()
})
// After the tick that showed the clicked tab: a reset in the same tick is no
// change at all for the Tabs component, which keeps the clicked tab selected.
const onNavigationKept = () => {
  nextTick(() => {
    tabValue.value = activeTabValue()
  })
}
onMounted(() => window.addEventListener(NAVIGATION_KEPT_EVENT, onNavigationKept))
onBeforeUnmount(() => window.removeEventListener(NAVIGATION_KEPT_EVENT, onNavigationKept))

const updateUrl = value => {
  tabValue.value = value
  if (value !== page.url) {
    router.visit(value)
  }
}
</script>